<script setup lang="ts">
import { computed, ref, onBeforeUnmount, watch, onMounted, inject } from 'vue';
import { useInfiniteScroll } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { createCommonLink } from '@arbocollab/arbo-ui-components';

import VisualInformation from 'src/components/elements/VisualInformation.vue';
import { baseLinkConfig, pressActionHandler } from 'src/plugins';
import { useGlobalSearchStore } from 'src/stores';
import { useApi } from 'src/stores/useApis';
import { useUserStore } from 'src/stores/userStore';
import type { PeopleCardData, SearchType } from 'src/types';
import type { ProjectCardData } from 'src/types/entity.types';
import type { SceneCardDataType } from 'src/types/scene.types';

// Props and refs
type Props = {
    type: SearchType;
};
const props = defineProps<Props>();

const eleRef = ref<HTMLElement | null>(null);
const listRef = ref<HTMLElement | null>(null);
const selectedIndex = ref(-1);
const selectMode = ref<'mouse' | 'keyboard'>('mouse');

// Store setup
const globalSearchStore = useGlobalSearchStore();
const { loadMoreDataFunc } = globalSearchStore;
const apiStore = useApi();
const { userPermissionConfig } = storeToRefs(useUserStore());

const { searchResults, currentPage, isSearching, canLoadMore, searchText, statusFilter, currentSearchTab } =
    storeToRefs(globalSearchStore);

// Computed properties
const emptyData = computed(() => {
    return searchResults.value?.length === 0 && searchText.value?.length === 0 && !isSearching.value;
});

const emptySearch = computed(() => {
    return searchResults.value?.length === 0 && searchText.value?.length > 0 && !isSearching.value;
});

const isMore = computed(() => {
    return !isSearching.value && canLoadMore.value;
});

const isShowLoading = computed(() => {
    return isSearching.value && !emptyData.value && !emptySearch.value;
});

// Watchers
watch(
    [() => statusFilter, currentSearchTab, searchText],
    async () => {
        isSearching.value = true;
        globalSearchStore.clearResult();
        await apiStore.cancelAllRequests();
        loadMoreDataFunc();
    },
    { deep: true }
);

watch([currentSearchTab, searchText, statusFilter], () => {
    selectedIndex.value = -1;
});

// Cleanup function
const cleanup = async () => {
    searchResults.value = [];
    currentPage.value = 0;
    await apiStore.cancelAllRequests();
    await globalSearchStore.clearResult();
};

// Navigation handlers
const switchInstance = inject('switchInstance') as any;
const handleOpenPeopleInsight = (_e: KeyboardEvent | MouseEvent, data: PeopleCardData) => {
    if (!userPermissionConfig.value.canOpenPeopleInsight) return;

    const people_id = data?.id || '';
    const link = createCommonLink({
        type: 'people_insight',
        config: {
            ...baseLinkConfig(),
            user_id: people_id
        }
    }).url;
    if (_e.metaKey || _e.ctrlKey) {
        window.open(link, '_blank');
        return;
    } else {
        if (!switchInstance) {
            window.location.href = link;
            return;
        }
        switchInstance('people_insight', { tab: 'home', id: people_id || '' }, { showLoading: false });
    }
};

const handleOpenReview = (_e: KeyboardEvent | MouseEvent, data: SceneCardDataType) => {
    if (!userPermissionConfig.value.canOpenReview) return;

    const scene_id = data?.id || '';
    const take_id = data?.entity_path[data.entity_path.length - 1].id;
    const link = createCommonLink({
        type: 'review_scene',
        config: {
            scene_id: scene_id,
            take_id: take_id,
            ...baseLinkConfig()
        }
    }).url;
    if (_e.metaKey || _e.ctrlKey) {
        window.open(link, '_blank');
        return;
    }
    switchInstance('scene_hub', { tab: 'home', id: scene_id }, { showLoading: false });
};

const openMode = (e: KeyboardEvent | MouseEvent, data: any) => {
    const { parent_id, id } = data || {};
    if (!id) return;

    const url = createCommonLink({
        type: 'project_focusMode',
        config: { ...baseLinkConfig(), entity_id: id, parent_id: parent_id || '' }
    }).url;
    pressActionHandler(e, url);
};

const handleOpenTakeProject = (e: KeyboardEvent | MouseEvent, data: ProjectCardData) => {
    if (!userPermissionConfig.value.canOpenProject) return;
    openMode(e, data);
};

const handelEnter = (event: KeyboardEvent | MouseEvent, card: (typeof searchResults.value)[0]) => {
    const actionMap = {
        people: () => handleOpenPeopleInsight(event, card as PeopleCardData),
        entity: () => handleOpenTakeProject(event, card as ProjectCardData),
        item: () => handleOpenReview(event, card as SceneCardDataType),
        recent_accessed: () => {
            const cardData = card as any;
            if (cardData.entity) {
                handleOpenTakeProject(event, cardData.entity as ProjectCardData);
            } else if (cardData.item) {
                handleOpenReview(event, cardData.item as SceneCardDataType);
            } else {
                handleOpenPeopleInsight(event, cardData.ws_user_role as PeopleCardData);
            }
        }
    };

    actionMap[props.type]?.();
};

// Keyboard navigation
const handleRedirectScroll = (id: string) => {
    const idEle = `${props.type}--${id}`;
    document.getElementById(idEle)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
    });
};

const handleKeyDown = (event: KeyboardEvent) => {
    if (!searchResults.value || searchResults.value.length === 0) return;

    selectMode.value = 'keyboard';

    const keyActions = {
        ArrowDown: () => {
            event.preventDefault();
            if (selectedIndex.value < searchResults.value.length - 1) {
                selectedIndex.value++;
                handleRedirectScroll(searchResults.value[selectedIndex.value].id);
            }
        },
        ArrowUp: () => {
            event.preventDefault();
            if (selectedIndex.value > 0) {
                selectedIndex.value--;
                handleRedirectScroll(searchResults.value[selectedIndex.value].id);
            }
        },
        Enter: () => {
            if (selectedIndex.value >= 0 && selectedIndex.value < searchResults.value.length) {
                const selectedItem = searchResults.value[selectedIndex.value];
                handelEnter(event, selectedItem);
            }
        },
        Escape: () => {
            selectedIndex.value = -1;
        }
    };

    keyActions[event.key as keyof typeof keyActions]?.();
};

const handleMouseEnter = (_e: KeyboardEvent | MouseEvent, index: number) => {
    if (selectMode.value === 'keyboard') return;
    selectedIndex.value = index;
    selectMode.value = 'mouse';
};

// Infinite scroll setup
useInfiniteScroll(
    eleRef,
    async () => {
        isSearching.value = true;
        await loadMoreDataFunc();
        isSearching.value = false;
    },
    {
        canLoadMore: () => isMore.value
    }
);

// Lifecycle hooks
onMounted(() => {
    document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(async () => {
    document.removeEventListener('keydown', handleKeyDown);
    isSearching.value = false;
    await cleanup();
    await loadMoreDataFunc();

    if (eleRef.value) {
        eleRef.value?.removeEventListener('scroll', loadMoreDataFunc);
    }
});
</script>

<template>
    <div
        :id="`list-search-common-${props.type}`"
        :key="`list-search-common-${props.type}`"
        ref="listRef"
        class="list-search-common"
        @mousemove="selectMode = 'mouse'">
        <div v-if="emptySearch" class="no-result">
            <VisualInformation :visual-name="'NoData2'" :label="'No results. \n Try another search.'" />
        </div>

        <div v-else-if="emptyData" class="no-data">
            <VisualInformation :visual-name="'NoData2'" :label="'Start typing to see results...'" />
        </div>

        <div ref="eleRef" class="list-data" :class="{ 'key-mode': selectMode === 'keyboard' }">
            <div
                v-for="(item, index) in searchResults"
                :id="`${props.type}--${item.id}`"
                :key="`${props.type}--${item.id}`"
                class="item"
                :class="{ active: selectedIndex === index }"
                @mouseenter="(e) => handleMouseEnter(e, index)"
                @click="(e) => handelEnter(e, item)">
                <slot name="item" :data="item" :active="selectedIndex === index"></slot>
            </div>

            <template v-if="isShowLoading">
                <div class="list-search-common-loading">
                    <div v-for="item in 5" :key="item">
                        <slot :key="item" name="skeleton-loading"></slot>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
